import { getNormalizedJurisdictionPolygons } from 'hooks/utils/location.utils';
import { describeOutsideCoordinate } from '../../../utils/ipawsArea.utils';

/** Returns whether a value contains latitude and longitude numbers that the map can use. */
export const isValidMapCoordinate = (coordinate) =>
  Number.isFinite(Number(coordinate?.lat)) && Number.isFinite(Number(coordinate?.lng));

// Check if two coordinates are the same, treating null/undefined as { lat: 0, lng: 0 }
const isSameCoordinate = (coordinate1, coordinate2) => {
  return (
    (coordinate1?.lat ?? 0) === (coordinate2?.lat ?? 0) &&
    (coordinate1?.lng ?? 0) === (coordinate2?.lng ?? 0)
  );
};

// Function to check if a point is inside a polygon using ray casting algorithm
export const isPointInPolygon = (point, polygon) => {
  const { lat, lng } = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [lat1, lng1] = polygon[i]; // polygon is [lat, lng] format after conversion
    const [lat2, lng2] = polygon[j];

    if (lat1 > lat !== lat2 > lat && lng < ((lng2 - lng1) * (lat - lat1)) / (lat2 - lat1) + lng1) {
      inside = !inside;
    }
  }

  return inside;
};

// Function to check if a marker is within any jurisdiction
export const isMarkerInJurisdiction = (latlng, jurisdictions) => {
  if (!jurisdictions || jurisdictions.length === 0) {
    return true; // If no jurisdictions defined, allow anywhere
  }

  try {
    const polygons = getNormalizedJurisdictionPolygons(jurisdictions);
    if (!polygons || polygons.length === 0) return true;

    for (const polygon of polygons) {
      if (isPointInPolygon(latlng, polygon)) return true;
    }
  } catch (error) {
    console.error('Error checking marker in jurisdiction:', error);
  }

  return false;
};

// Function to validate that all coordinates are within jurisdiction boundaries
export const validateJurisdictionBoundaries = (coordinates, jurisdictions) => {
  if (!coordinates || coordinates.length === 0) return true;

  for (let coordinate of coordinates) {
    if (!isMarkerInJurisdiction(coordinate, jurisdictions)) {
      return false;
    }
  }
  return true;
};

/** The name the warnings give the one RescAlert alert area. */
export const RESCALERT_AREA_LABEL = 'Alert area';

/**
 * Finds each RescAlert coordinate that is outside the workplace jurisdiction. It uses the same
 * point check as `validateJurisdictionBoundaries`, so the map and the form agree.
 * @param {Array<{ lat: number, lng: number }>} coordinates The alert area vertices.
 * @param {object[]} jurisdictions The workplace jurisdictions.
 * @returns {{ key: string, position: object, message: string, accessibleName: string }[]}
 */
export const getRescAlertOutsideCoordinates = (coordinates, jurisdictions) =>
  (Array.isArray(coordinates) ? coordinates : [])
    .map((position, index) => ({ position, number: index + 1, name: '' }))
    .filter(
      (checkPoint) =>
        isValidMapCoordinate(checkPoint.position) &&
        !isMarkerInJurisdiction(checkPoint.position, jurisdictions),
    )
    .map((checkPoint) => ({
      key: `rescalert-${checkPoint.number}`,
      position: checkPoint.position,
      ...describeOutsideCoordinate({ areaLabel: RESCALERT_AREA_LABEL, checkPoint }),
    }));

// Calculates distance between two points
export const calculateDistance = (latlng1, latlng2) => {
  if (!latlng1 || !latlng2) return 0;

  const latDiff = (latlng2?.lat ?? 0) - (latlng1?.lat ?? 0);
  const lngDiff = (latlng2?.lng ?? 0) - (latlng1?.lng ?? 0);

  return Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
};

// Adds a new alert point (marker) to the alert data, placing it at the beginning or end of the coordinates array based on proximity
export const addAlertPoint = (alertData, latlng) => {
  if (!latlng) return alertData;

  const alertDataOrEmpty = alertData ?? {};
  const coordinates = alertDataOrEmpty?.coordinates ?? [];
  const lastInserted = alertDataOrEmpty?.lastInserted ?? [];

  // If there are no existing coordinates, just add the new point
  if (!coordinates || !coordinates.length) {
    return {
      ...alertDataOrEmpty,
      coordinates: [latlng],
      lastInserted: [latlng],
    };
  }

  const firstCoordinate = coordinates[0];
  const lastCoordinate = coordinates[coordinates.length - 1];
  const distanceToFirst = calculateDistance(latlng, firstCoordinate);
  const distanceToLast = calculateDistance(latlng, lastCoordinate);

  return {
    ...alertDataOrEmpty,
    coordinates:
      distanceToFirst < distanceToLast ? [latlng, ...coordinates] : [...coordinates, latlng],
    lastInserted: [...lastInserted, latlng],
  };
};

// Moves an existing alert point (marker) to a new position after dragging, updating both coordinates and lastInserted arrays
export const moveAlertPoint = (alertData, index, newPosition) => {
  const alertDataOrEmpty = alertData ?? {};
  const coordinates = alertDataOrEmpty?.coordinates ?? [];

  if (!coordinates || !coordinates.length || index < 0 || index >= coordinates.length) {
    return alertData;
  }

  const previousPosition = coordinates[index];

  // Updates the position of the dragged marker and keeps the rest unchanged
  const updatedCoordinates = coordinates.map((coordinate, coordinateIndex) =>
    coordinateIndex === index ? newPosition : coordinate,
  );

  let replacedCoordinateWithinLastInserted = false;

  // Replace the previous position with the new position in lastInserted array and keep the rest unchanged
  const updatedLastInserted = (alertDataOrEmpty?.lastInserted ?? []).map((coordinate) => {
    if (!replacedCoordinateWithinLastInserted && isSameCoordinate(coordinate, previousPosition)) {
      replacedCoordinateWithinLastInserted = true;
      return newPosition;
    }

    return coordinate;
  });

  return {
    ...alertDataOrEmpty,
    coordinates: updatedCoordinates,
    lastInserted: updatedLastInserted,
  };
};
